import { c as _c } from "react/compiler-runtime";
import * as Sentry from '@sentry/nextjs';
import { usePathname } from 'next/navigation';
import React, { type ErrorInfo, type ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';
export { ErrorFallback } from './ErrorFallback';
export type { ErrorFallbackProps } from './ErrorFallback';
type FallbackRenderArgs = {
  error: Error;
  reset: () => void;
  eventId: string | null;
};
type AppErrorBoundaryProps = {
  children: ReactNode;
  /** Tag sent to Sentry as `error_boundary` for triage */
  name?: string;
  fallback?: (args: FallbackRenderArgs) => ReactNode;
  onError?: (error: Error, info: ErrorInfo, eventId: string | null) => void;
};
type AppErrorBoundaryState = {
  error: Error | null;
  eventId: string | null;
};

/**
 * Catches render/lifecycle errors in children, reports to Sentry, and shows a recovery UI
 * instead of letting Next replace the whole document.
 */
export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    error: null,
    eventId: null
  };
  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return {
      error
    };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    let eventId: string | null = null;
    Sentry.withScope(scope => {
      scope.setTag('error_boundary', this.props.name ?? 'app');
      scope.setExtra('componentStack', info.componentStack);
      eventId = Sentry.captureException(error);
    });
    this.setState({
      eventId
    });
    this.props.onError?.(error, info, eventId);
  }
  reset = () => {
    this.setState({
      error: null,
      eventId: null
    });
  };
  render() {
    const {
      error,
      eventId
    } = this.state;
    if (!error) {
      return this.props.children;
    }
    if (this.props.fallback) {
      return this.props.fallback({
        error,
        reset: this.reset,
        eventId
      });
    }
    return <ErrorFallback error={error} eventId={eventId} variant={'page'} onReset={this.reset} />;
  }
}
type RouteErrorBoundaryProps = {
  children: ReactNode;
  name?: string;
};

/**
 * App-level boundary that remounts on client route changes so a previous error
 * does not stick after navigation.
 */
export function RouteErrorBoundary(t0) {
  const $ = _c(4);
  const {
    children,
    name: t1
  } = t0;
  const name = t1 === undefined ? "app-page" : t1;
  const pathname = usePathname();
  const t2 = pathname ?? "app";
  let t3;
  if ($[0] !== children || $[1] !== name || $[2] !== t2) {
    t3 = <AppErrorBoundary key={t2} name={name}>{children}</AppErrorBoundary>;
    $[0] = children;
    $[1] = name;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
type SectionErrorBoundaryProps = {
  children: ReactNode;
  name: string;
  title?: string;
  description?: string;
};

/**
 * Nested boundary for high-risk widgets so one section can fail without taking
 * down the rest of the page. Usage:
 *
 * ```tsx
 * <SectionErrorBoundary name="pitch-deck-auditor">
 *   <PitchDeckAuditor />
 * </SectionErrorBoundary>
 * ```
 */
export function SectionErrorBoundary(t0) {
  const $ = _c(7);
  const {
    children,
    name,
    title: t1,
    description: t2
  } = t0;
  const title = t1 === undefined ? "This section failed to load" : t1;
  const description = t2 === undefined ? "We've been notified. You can retry this section or keep using the rest of the page." : t2;
  let t3;
  if ($[0] !== description || $[1] !== title) {
    t3 = t4 => {
      const {
        error,
        reset,
        eventId
      } = t4;
      return <ErrorFallback description={description} error={error} eventId={eventId} showHomeLink={false} title={title} variant="section" onReset={reset} />;
    };
    $[0] = description;
    $[1] = title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== name || $[5] !== t3) {
    t4 = <AppErrorBoundary name={name} fallback={t3}>{children}</AppErrorBoundary>;
    $[3] = children;
    $[4] = name;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}